import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTrip } from '../contexts/TripContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { 
  MapPin, Loader2, ArrowLeft, ArrowRight, 
  Vote, CheckCircle2, Plus, X, ImageIcon, Sparkles, Camera, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import PlaceAutocomplete from '../components/common/PlaceAutocomplete';
import CoverImageUploader from '../components/common/CoverImageUploader';
import ResponsiveDatePicker from '../components/common/ResponsiveDatePicker';

// Preset cover images for trips
const PRESET_COVER_IMAGES = [
  { id: 1, url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800', label: 'Praia' },
  { id: 2, url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800', label: 'Montanha' },
  { id: 3, url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800', label: 'Cidade' },
  { id: 4, url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800', label: 'Natureza' },
  { id: 5, url: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800', label: 'Estrada' },
];

export default function CreateTrip() {
  const navigate = useNavigate();
  const location = useLocation();
  const { createTrip, selectWinningDestination, updateTrip } = useTrip();
  const { t, language } = useLanguage();
  const { api } = useAuth();
  const dateLocale = language === 'en-US' ? enUS : ptBR;
  const [loading, setLoading] = useState(false);
  
  // Check if we're finalizing a voting trip
  const finalizeVotingData = location.state?.finalizeVoting;
  const isFinalizeMode = !!finalizeVotingData;
  
  // Start at step 1 (skip destination type) if finalizing voting
  const [step, setStep] = useState(isFinalizeMode ? 1 : 0);
  
  // Destination type: 'defined' or 'voting'
  const [destinationType, setDestinationType] = useState(isFinalizeMode ? 'defined' : null);
  
  // For voting mode - multiple destination options (max 3)
  const [destinationOptions, setDestinationOptions] = useState([
    { id: 1, name: '', image: '', place: null, number: '', complement: '' },
    { id: 2, name: '', image: '', place: null, number: '', complement: '' },
  ]);
  
  // Initialize form data with voting data if available
  const getInitialFormData = () => {
    if (finalizeVotingData) {
      const { tripName, selectedOption, tripId, startDate, endDate, coverImage, origin, originPlace, description } = finalizeVotingData;
      return {
        name: tripName || '',
        origin: origin || '',
        origin_place: originPlace || null,
        origin_number: '',
        origin_complement: '',
        destination: selectedOption?.name || '',
        destination_place: selectedOption?.place_data || null,
        destination_number: '',
        destination_complement: '',
        description: description || selectedOption?.description || '',
        start_date: startDate ? new Date(startDate) : null,
        end_date: endDate ? new Date(endDate) : null,
        cover_image: coverImage || selectedOption?.image || '',
      };
    }
    return {
      name: '',
      origin: '',
      origin_place: null,
      origin_number: '',
      origin_complement: '',
      destination: '',
      destination_place: null,
      destination_number: '',
      destination_complement: '',
      description: '',
      start_date: null,
      end_date: null,
      cover_image: '',
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData);
  
  // Deferred upload: store the blob locally until form submission
  const [coverImageBlob, setCoverImageBlob] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDestinationOptionChange = (id, field, value) => {
    setDestinationOptions(options =>
      options.map(opt => opt.id === id ? { ...opt, [field]: value } : opt)
    );
  };

  const addDestinationOption = () => {
    // Limit to maximum 3 options
    if (destinationOptions.length >= 3) {
      toast.error('Máximo de 3 opções de destino');
      return;
    }
    const newId = Math.max(...destinationOptions.map(o => o.id)) + 1;
    setDestinationOptions([...destinationOptions, { id: newId, name: '', image: '', place: null, number: '', complement: '' }]);
  };

  const removeDestinationOption = (id) => {
    if (destinationOptions.length > 2) {
      setDestinationOptions(options => options.filter(o => o.id !== id));
    }
  };

  // Helper function to upload a blob and get URL
  const uploadImageBlob = async (blob) => {
    if (!blob) return null;
    const uploadFormData = new FormData();
    uploadFormData.append('file', blob, 'cover.jpg');
    const response = await api.post('/storage/upload-image-direct', uploadFormData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.file_url;
  };

  const handleSubmit = async () => {
    // Validate based on mode
    if (destinationType === 'defined' || isFinalizeMode) {
      if (!formData.name || !formData.origin || !formData.destination || !formData.start_date || !formData.end_date) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
    } else {
      // Voting mode - only name is required
      if (!formData.name) {
        toast.error('Dê um nome para sua viagem');
        return;
      }
      const validOptions = destinationOptions.filter(o => o.name.trim());
      if (validOptions.length < 2) {
        toast.error('Adicione pelo menos 2 opções de destino');
        return;
      }
    }

    setLoading(true);
    try {
      // Upload deferred cover image blob if exists
      let finalCoverImage = formData.cover_image;
      if (coverImageBlob) {
        // Check if current cover_image is a blob URL (starts with blob:)
        if (formData.cover_image?.startsWith('blob:')) {
          finalCoverImage = await uploadImageBlob(coverImageBlob);
        }
      }
      // Helper function to extract address components from Google Places data
      const buildAddressData = (place, number, complement) => {
        if (!place) return null;
        
        // Parse address components if available
        const getComponent = (types) => {
          if (!place.address_components) return null;
          const component = place.address_components.find(c => 
            types.some(t => c.types.includes(t))
          );
          return component?.long_name || null;
        };
        
        const getComponentShort = (types) => {
          if (!place.address_components) return null;
          const component = place.address_components.find(c => 
            types.some(t => c.types.includes(t))
          );
          return component?.short_name || null;
        };
        
        return {
          place_name: place.name || null,
          formatted_address: place.formatted_address || null,
          place_id: place.place_id || null,
          street: getComponent(['route', 'street_address']) || null,
          number: number || getComponent(['street_number']) || null,
          complement: complement || null,
          neighborhood: getComponent(['sublocality', 'sublocality_level_1', 'neighborhood']) || null,
          city: getComponent(['administrative_area_level_2', 'locality']) || null,
          state: getComponent(['administrative_area_level_1']) || null,
          state_code: getComponentShort(['administrative_area_level_1']) || null,
          country: getComponent(['country']) || null,
          country_code: getComponentShort(['country']) || null,
          postal_code: getComponent(['postal_code']) || null,
          lat: place.lat || null,
          lng: place.lng || null,
          google_maps_url: place.google_maps_url || null
        };
      };

      // Build destination data with complete address
      const destinationData = buildAddressData(
        formData.destination_place, 
        formData.destination_number, 
        formData.destination_complement
      );

      // Build origin data with complete address
      const originData = buildAddressData(
        formData.origin_place, 
        formData.origin_number, 
        formData.origin_complement
      );

      // If we're finalizing a voting trip
      if (isFinalizeMode && finalizeVotingData) {
        const { tripId, selectedOption } = finalizeVotingData;
        
        // Update the trip with the complete data
        await updateTrip(tripId, {
          name: formData.name,
          origin: formData.origin,
          origin_data: originData,
          description: formData.description || null,
          start_date: formData.start_date?.toISOString(),
          end_date: formData.end_date?.toISOString(),
          cover_image: finalCoverImage || null,
        });
        
        // Select the winning destination (this will change status to 'planning')
        await selectWinningDestination(tripId, selectedOption.id);
        
        toast.success(`Viagem "${formData.name}" confirmada! 🎉`);
        navigate(`/trip/${tripId}`);
        return;
      }

      const tripData = {
        name: formData.name,
        destination: destinationType === 'defined' ? formData.destination : 'A definir por votação',
        destination_data: destinationType === 'defined' ? destinationData : null,
        origin: formData.origin || null,
        origin_data: originData,
        description: formData.description || null,
        start_date: formData.start_date?.toISOString() || null,
        end_date: formData.end_date?.toISOString() || null,
        cover_image: finalCoverImage || null,
        status: destinationType === 'voting' ? 'voting' : 'planning',
        destination_voting: destinationType === 'voting' ? {
          options: destinationOptions.filter(o => o.name.trim()).map(o => ({
            id: o.id.toString(),
            name: o.name,
            image: o.image || null,
            votes: [],
            // Include complete address data for voting options
            place_data: buildAddressData(o.place, o.number, o.complement)
          })),
          is_open: true,
          selected_option: null
        } : null
      };

      const trip = await createTrip(tripData);
      
      // Mark trip as just created for showing the tutorial
      sessionStorage.setItem(`trip_just_created_${trip.id}`, 'true');
      
      if (destinationType === 'voting') {
        toast.success('Viagem criada! Convide os amigos para votar no destino! 🗳️');
      } else {
        toast.success('Viagem criada com sucesso! ✈️');
      }
      
      navigate(`/trip/${trip.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar viagem';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 0) {
      return destinationType !== null;
    }
    if (step === 1) {
      // Finalize mode - all fields must be filled in step 1
      if (isFinalizeMode) {
        return formData.name && formData.origin && formData.start_date && formData.end_date;
      }
      if (destinationType === 'voting') {
        return formData.name && destinationOptions.filter(o => o.name.trim()).length >= 2;
      }
      return formData.name && formData.origin && formData.destination;
    }
    if (step === 2) {
      // Dates are optional for both modes but required for defined mode
      if (destinationType === 'voting') {
        return true; // Dates are optional for voting mode
      }
      return formData.start_date && formData.end_date;
    }
    if (step === 3) {
      return true; // Description is optional
    }
    return true;
  };

  const handleNext = () => {
    if (canProceed()) {
      // Finalize mode - submit on step 1
      if (isFinalizeMode && step === 1) {
        handleSubmit();
        return;
      }
      // For voting mode, now we have 3 steps, submit on step 3
      if (destinationType === 'voting' && step === 3) {
        handleSubmit();
      } else if (destinationType === 'defined' && step === 3) {
        handleSubmit();
      } else {
        setStep(step + 1);
      }
    }
  };

  const handleKeyDown = (e) => {
    // Prevent form submission on Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canProceed()) {
        handleNext();
      }
    }
  };

  const totalSteps = 4; // Both modes now have 4 steps (0, 1, 2, 3)
  const currentStepDisplay = Math.min(step, totalSteps - 1);

  return (
    <DashboardLayout title="Nova Viagem">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Progress Steps */}
          {step > 0 && (
            <div className="flex items-center justify-center mb-8">
              {Array.from({ length: totalSteps - 1 }, (_, i) => i + 1).map((s) => (
                <React.Fragment key={s}>
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors',
                      currentStepDisplay >= s
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                    )}
                  >
                    {s}
                  </div>
                  {s < totalSteps - 1 && (
                    <div
                      className={cn(
                        'w-16 h-1 mx-2 rounded-full transition-colors',
                        currentStepDisplay > s ? 'bg-orange-500' : 'bg-slate-100 dark:bg-slate-800'
                      )}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          <Card className="border-slate-100 dark:border-slate-800 dark:bg-slate-900">
            <CardHeader>
              {/* Show finalize mode banner */}
              {isFinalizeMode && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <Trophy className="w-5 h-5" />
                    <span className="font-medium">Finalizando votação</span>
                  </div>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    Destino escolhido: <strong>{finalizeVotingData?.selectedOption?.name}</strong>
                  </p>
                </div>
              )}
              <CardTitle className="font-heading dark:text-white">
                {step === 0 && 'Como você quer começar?'}
                {step === 1 && isFinalizeMode && 'Complete a viagem'}
                {step === 1 && destinationType === 'defined' && !isFinalizeMode && 'Informações básicas'}
                {step === 1 && destinationType === 'voting' && !isFinalizeMode && 'Votação de destino'}
                {step === 2 && destinationType === 'defined' && 'Datas da viagem'}
                {step === 2 && destinationType === 'voting' && 'Datas (opcional)'}
                {step === 3 && 'Descrição'}
              </CardTitle>
              <CardDescription className="dark:text-slate-400">
                {step === 0 && 'Você já sabe o destino ou quer que o grupo decida?'}
                {step === 1 && isFinalizeMode && 'Preencha a origem e as datas para confirmar a viagem'}
                {step === 1 && destinationType === 'defined' && !isFinalizeMode && 'Dê um nome e escolha origem e destino'}
                {step === 1 && destinationType === 'voting' && !isFinalizeMode && 'Adicione as opções de destino para votação'}
                {step === 2 && destinationType === 'defined' && 'Quando será a sua viagem?'}
                {step === 2 && destinationType === 'voting' && 'Defina as datas ou deixe para depois'}
                {step === 3 && 'Conte mais sobre a viagem'}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <div onKeyDown={handleKeyDown} className="space-y-6">
                {/* Step 0: Destination Type Selection */}
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      key="step0"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <RadioGroup 
                        value={destinationType} 
                        onValueChange={setDestinationType}
                        className="space-y-4"
                      >
                        <label
                          className={cn(
                            'flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all',
                            destinationType === 'defined'
                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                          )}
                        >
                          <RadioGroupItem value="defined" id="defined" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                              <span className="font-semibold text-slate-900 dark:text-white">
                                Já sei o destino
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              Tenho o destino definido e quero criar a viagem completa agora
                            </p>
                          </div>
                        </label>

                        <label
                          className={cn(
                            'flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all',
                            destinationType === 'voting'
                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                          )}
                        >
                          <RadioGroupItem value="voting" id="voting" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Vote className="w-5 h-5 text-purple-500" />
                              <span className="font-semibold text-slate-900 dark:text-white">
                                Quero que o grupo decida
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              Criar uma votação com opções de destino para o grupo escolher
                            </p>
                          </div>
                        </label>
                      </RadioGroup>
                    </motion.div>
                  )}

                  {/* Step 1: Basic Info (Defined) or Voting Options */}
                  {step === 1 && (destinationType === 'defined' || isFinalizeMode) && (
                    <motion.div
                      key="step1-defined"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="name" className="dark:text-slate-300">Nome da viagem *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Ex: Férias de verão 2024"
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-1 dark:bg-slate-800 dark:border-slate-700"
                          data-testid="trip-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="origin" className="dark:text-slate-300">
                          Origem *
                        </Label>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                          De onde o grupo vai partir?
                        </p>
                        <div className="mt-1">
                          <PlaceAutocomplete
                            value={formData.origin_place}
                            onChange={(place) => {
                              setFormData({
                                ...formData,
                                origin: place?.name || place?.formatted_address || '',
                                origin_place: place
                              });
                            }}
                            placeholder="Cidade ou endereço de partida..."
                            showMap={true}
                            data-testid="trip-origin"
                          />
                        </div>
                        {/* Optional Number and Complement fields for Origin */}
                        {formData.origin_place && (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <Input
                                placeholder="Número (opcional)"
                                value={formData.origin_number}
                                onChange={(e) => setFormData({...formData, origin_number: e.target.value})}
                                className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                                data-testid="trip-origin-number"
                              />
                            </div>
                            <div>
                              <Input
                                placeholder="Complemento (opcional)"
                                value={formData.origin_complement}
                                onChange={(e) => setFormData({...formData, origin_complement: e.target.value})}
                                className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                                data-testid="trip-origin-complement"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Destination field - locked if finalizing */}
                      {isFinalizeMode ? (
                        <div>
                          <Label className="dark:text-slate-300">Destino (definido por votação)</Label>
                          <div className="mt-1 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="flex items-center gap-3">
                              {finalizeVotingData?.selectedOption?.image && (
                                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                                  <img 
                                    src={finalizeVotingData.selectedOption.image} 
                                    alt={finalizeVotingData.selectedOption.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-green-500" />
                                  <span className="font-medium text-slate-900 dark:text-white">
                                    {formData.destination}
                                  </span>
                                </div>
                                {finalizeVotingData?.selectedOption?.place_data?.formatted_address && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                                    {finalizeVotingData.selectedOption.place_data.formatted_address}
                                  </p>
                                )}
                              </div>
                              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="destination" className="dark:text-slate-300">Destino *</Label>
                          <div className="mt-1">
                          <PlaceAutocomplete
                            value={formData.destination_place}
                            onChange={(place) => {
                              setFormData({
                                ...formData,
                                destination: place?.name || place?.formatted_address || '',
                                destination_place: place
                              });
                            }}
                            placeholder="Cidade, endereço ou local..."
                            showMap={true}
                            data-testid="trip-destination"
                          />
                        </div>
                        {/* Optional Number and Complement fields for Destination */}
                        {formData.destination_place && (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <Input
                                placeholder="Número (opcional)"
                                value={formData.destination_number}
                                onChange={(e) => setFormData({...formData, destination_number: e.target.value})}
                                className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                                data-testid="trip-destination-number"
                              />
                            </div>
                            <div>
                              <Input
                                placeholder="Complemento (opcional)"
                                value={formData.destination_complement}
                                onChange={(e) => setFormData({...formData, destination_complement: e.target.value})}
                                className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                                data-testid="trip-destination-complement"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                      
                      {/* Date fields for finalize mode - shown inline */}
                      {isFinalizeMode && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="dark:text-slate-300">Data de ida *</Label>
                              <div className="mt-1">
                                <ResponsiveDatePicker
                                  value={formData.start_date}
                                  onChange={(date) => setFormData({ ...formData, start_date: date })}
                                  placeholder="Selecionar..."
                                  minDate={new Date()}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="dark:text-slate-300">Data de volta *</Label>
                              <div className="mt-1">
                                <ResponsiveDatePicker
                                  value={formData.end_date}
                                  onChange={(date) => setFormData({ ...formData, end_date: date })}
                                  placeholder="Selecionar..."
                                  minDate={formData.start_date || new Date()}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Description for finalize mode */}
                          <div>
                            <Label htmlFor="description" className="dark:text-slate-300">Descrição (opcional)</Label>
                            <Textarea
                              id="description"
                              name="description"
                              placeholder="Conte mais sobre a viagem..."
                              value={formData.description}
                              onChange={handleChange}
                              className="mt-1 dark:bg-slate-800 dark:border-slate-700"
                              rows={3}
                              data-testid="trip-description"
                            />
                          </div>
                        </>
                      )}
                    </motion.div>
                  )}

                  {step === 1 && destinationType === 'voting' && !isFinalizeMode && (
                    <motion.div
                      key="step1-voting"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="name" className="dark:text-slate-300">Nome da viagem *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Ex: Viagem de fim de ano - Para onde vamos?"
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-1 dark:bg-slate-800 dark:border-slate-700"
                          data-testid="trip-name"
                        />
                      </div>

                      {/* Cover Image for the trip - single image for voting mode */}
                      <div>
                        <Label className="dark:text-slate-300 flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          Imagem de capa (opcional)
                        </Label>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-3">
                          Faça upload de uma imagem ou escolha uma das opções predefinidas
                        </p>

                        {/* Upload Component with Cropping - Deferred upload */}
                        <CoverImageUploader
                          currentCoverUrl={formData.cover_image}
                          onCoverChange={(url) => setFormData(prev => ({ ...prev, cover_image: url }))}
                          deferUpload={true}
                          onBlobReady={(blob) => setCoverImageBlob(blob)}
                          className="mb-3"
                        />

                        {/* Preset Images Grid */}
                        <div className="grid grid-cols-5 gap-2">
                          {PRESET_COVER_IMAGES.map((img) => (
                            <button
                              key={img.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, cover_image: img.url });
                                setCoverImageBlob(null); // Clear blob when selecting preset
                              }}
                              className={cn(
                                'relative aspect-video rounded-lg overflow-hidden border-2 transition-all',
                                formData.cover_image === img.url
                                  ? 'border-orange-500 ring-2 ring-orange-200 dark:ring-orange-800'
                                  : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                              )}
                              title={img.label}
                            >
                              <img
                                src={img.url}
                                alt={img.label}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>

                        {/* Preview with remove button */}
                        {formData.cover_image && (
                          <div className="mt-3 relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                            <img
                              src={formData.cover_image}
                              alt="Preview da capa"
                              className="w-full h-32 object-cover"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setFormData(prev => ({ ...prev, cover_image: '' }))}
                              className="absolute top-2 right-2 bg-white/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800 h-8 w-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="pt-2">
                        <Label className="dark:text-slate-300 flex items-center gap-2">
                          <Vote className="w-4 h-4 text-purple-500" />
                          Opções de destino (mínimo 2, máximo 3)
                        </Label>
                        <div className="space-y-4 mt-3">
                          {destinationOptions.map((option, index) => (
                            <motion.div
                              key={option.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  Opção {index + 1}
                                </span>
                                {destinationOptions.length > 2 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeDestinationOption(option.id)}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                              
                              <div className="space-y-3">
                                {/* Place Autocomplete with Map */}
                                <PlaceAutocomplete
                                  value={option.place}
                                  onChange={(place) => {
                                    handleDestinationOptionChange(option.id, 'name', place?.name || place?.formatted_address || '');
                                    handleDestinationOptionChange(option.id, 'place', place);
                                  }}
                                  placeholder="Cidade, endereço ou local..."
                                  showMap={true}
                                  data-testid={`voting-option-${index}`}
                                />
                                
                                {/* Optional Number and Complement fields */}
                                {option.place && (
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      placeholder="Número (opcional)"
                                      value={option.number}
                                      onChange={(e) => handleDestinationOptionChange(option.id, 'number', e.target.value)}
                                      className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                                      data-testid={`voting-option-${index}-number`}
                                    />
                                    <Input
                                      placeholder="Complemento (opcional)"
                                      value={option.complement}
                                      onChange={(e) => handleDestinationOptionChange(option.id, 'complement', e.target.value)}
                                      className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                                      data-testid={`voting-option-${index}-complement`}
                                    />
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        
                        {destinationOptions.length < 3 && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addDestinationOption}
                            className="mt-3 w-full rounded-xl dark:border-slate-700"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Adicionar opção ({destinationOptions.length}/3)
                          </Button>
                        )}
                      </div>

                      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                        <div className="flex items-start gap-3">
                          <Sparkles className="w-5 h-5 text-purple-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-purple-900 dark:text-purple-300">
                              {t('createTrip.howItWorks')}
                            </p>
                            <p className="text-sm text-purple-700 dark:text-purple-400 mt-1">
                              {t('createTrip.votingExplanation')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Dates */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="dark:text-slate-300">
                            Data de ida {destinationType === 'defined' ? '*' : '(opcional)'}
                          </Label>
                          <div className="mt-1">
                            <ResponsiveDatePicker
                              value={formData.start_date}
                              onChange={(date) => {
                                setFormData(prev => ({
                                  ...prev,
                                  start_date: date,
                                  // Reset end_date if it's before new start_date
                                  end_date: prev.end_date && prev.end_date < date ? date : prev.end_date
                                }));
                              }}
                              placeholder="Selecione"
                              label="Data de ida"
                              disabled={(date) => date < new Date()}
                              locale={language}
                              data-testid="start-date-btn"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="dark:text-slate-300">
                            Data de volta {destinationType === 'defined' ? '*' : '(opcional)'}
                          </Label>
                          <div className="mt-1">
                            <ResponsiveDatePicker
                              value={formData.end_date}
                              onChange={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                              placeholder="Selecione"
                              label="Data de volta"
                              disabled={(date) => {
                                // End date must be >= start date (can be same day)
                                const minDate = formData.start_date || new Date();
                                const startOfMinDate = new Date(minDate);
                                startOfMinDate.setHours(0, 0, 0, 0);
                                return date < startOfMinDate;
                              }}
                              locale={language}
                              data-testid="end-date-btn"
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {destinationType === 'voting' 
                          ? 'As datas podem ser definidas depois que o destino for escolhido'
                          : 'A data de volta pode ser igual à data de ida para viagens de um dia'
                        }
                      </p>
                    </motion.div>
                  )}

                  {/* Step 3: Cover Image & Description */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <Label htmlFor="description" className="dark:text-slate-300">Descrição (opcional)</Label>
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Conte mais sobre a viagem..."
                          value={formData.description}
                          onChange={handleChange}
                          className="mt-1 dark:bg-slate-800 dark:border-slate-700"
                          rows={3}
                          data-testid="trip-description"
                        />
                      </div>

                      {/* Cover Image Selection - Only show for defined mode */}
                      {destinationType === 'defined' && (
                        <div>
                          <Label className="dark:text-slate-300 flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            Imagem de capa (opcional)
                          </Label>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-3">
                            Faça upload de uma imagem ou escolha uma das opções predefinidas
                          </p>

                          {/* Upload Component with Cropping - Deferred upload */}
                          <CoverImageUploader
                            currentCoverUrl={formData.cover_image}
                            onCoverChange={(url) => setFormData(prev => ({ ...prev, cover_image: url }))}
                            deferUpload={true}
                            onBlobReady={(blob) => setCoverImageBlob(blob)}
                            className="mb-4"
                          />

                          {/* Preset Images Grid */}
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                            Ou escolha uma imagem predefinida:
                          </p>
                          <div className="grid grid-cols-5 gap-2 mb-4">
                            {PRESET_COVER_IMAGES.map((img) => (
                              <button
                                key={img.id}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, cover_image: img.url });
                                  setCoverImageBlob(null); // Clear blob when selecting preset
                                }}
                                className={cn(
                                  'relative aspect-video rounded-lg overflow-hidden border-2 transition-all',
                                  formData.cover_image === img.url
                                    ? 'border-orange-500 ring-2 ring-orange-200 dark:ring-orange-800'
                                  : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                              )}
                            >
                              <img
                                src={img.url}
                                alt={img.label}
                                className="w-full h-full object-cover"
                              />
                              {formData.cover_image === img.url && (
                                <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                                  <CheckCircle2 className="w-5 h-5 text-white drop-shadow-lg" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>

                        {/* Preview with remove button */}
                        {formData.cover_image && (
                          <div className="mt-4 relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                            <img
                              src={formData.cover_image}
                              alt="Preview da capa"
                              className="w-full h-32 object-cover"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setFormData(prev => ({ ...prev, cover_image: '' }))}
                              className="absolute top-2 right-2 bg-white/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800 h-8 w-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  {step > 0 ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(step - 1)}
                      className="rounded-full dark:border-slate-700"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="rounded-full dark:border-slate-700"
                    >
                      Cancelar
                    </Button>
                  )}

                  {/* For voting mode on step 3, show "Criar Votação" */}
                  {destinationType === 'voting' && step === 3 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={loading || !canProceed()}
                      className="rounded-full bg-purple-500 hover:bg-purple-600"
                      data-testid="create-voting-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Vote className="w-4 h-4 mr-2" />
                          Criar votação
                        </>
                      )}
                    </Button>
                  ) : step === 3 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={loading}
                      className="rounded-full bg-orange-500 hover:bg-orange-600"
                      data-testid="create-trip-submit"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Criar viagem
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={!canProceed()}
                      className="rounded-full bg-orange-500 hover:bg-orange-600"
                      data-testid="next-step-btn"
                    >
                      Próximo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
